import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, CheckCircle2, Circle, Play, Lock, FileText, ListChecks, FolderOpen } from 'lucide-react';
import { useLang, pickLocalized, tx } from '../../i18n/LanguageContext';
import { fmtClock, lessonSecs } from '../../lib/curriculum';
import { stripHtml } from '../../lib/richText';
import { ytId } from '../course-editor/content/meta';

const Thumb = ({ lesson }) => {
  const yt = (lesson.type || 'video') === 'video' ? ytId(lesson.youtube) : '';
  if (yt) return <img src={`https://img.youtube.com/vi/${yt}/mqdefault.jpg`} alt="" loading="lazy" className="w-[72px] h-[46px] rounded-lg object-cover shrink-0 bg-slate-100" />;
  const Ic = lesson.type === 'quiz' ? ListChecks : lesson.type === 'text' ? FileText : Play;
  return <span className="w-[72px] h-[46px] rounded-lg shrink-0 bg-blue-50 text-blue-600 flex items-center justify-center"><Ic className="w-5 h-5" /></span>;
};

const Row = ({ lesson, idx, active, done, locked, onClick }) => {
  const { lang } = useLang();
  return (
    <button type="button" data-testid={`curriculum-lesson-${lesson.id}`} onClick={onClick} aria-current={active ? 'true' : undefined} title={pickLocalized(lesson.title, lang)}
      className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-start transition-colors ${active ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
      {active && <span className="absolute inset-y-0 end-0 w-[3px] rounded-full bg-blue-600" />}
      <span className="shrink-0 w-6 flex justify-center">
        {done ? <CheckCircle2 data-testid={`lesson-done-${lesson.id}`} className="w-6 h-6 text-white fill-emerald-500" />
          : locked ? <Lock className="w-4 h-4 text-amber-500" />
          : active ? null
          : <Circle className="w-6 h-6 text-slate-300" strokeWidth={1.5} />}
      </span>
      <span className="flex-1 min-w-0">
        <span className={`block text-[13px] leading-snug line-clamp-2 ${active ? 'text-blue-700 font-bold' : 'text-slate-800 font-semibold'}`} dir="auto">{pickLocalized(lesson.title, lang)}</span>
        {lessonSecs(lesson) ? <span className="block text-xs text-slate-400 mt-1 text-start" dir="ltr">{fmtClock(lessonSecs(lesson))}</span> : null}
      </span>
      <Thumb lesson={lesson} />
      <span className={`shrink-0 w-10 flex items-center justify-end gap-1 text-sm ${active ? 'text-blue-600 font-bold' : 'text-slate-600'}`}>
        {active && !done && <span className="w-[18px] h-[18px] rounded-full bg-blue-600 text-white flex items-center justify-center"><Play className="w-2 h-2 fill-white" /></span>}
        {(lesson.flatIndex ?? idx) + 1}
      </span>
    </button>
  );
};

export const LearnSidebar = ({ course, groups, flat, activeId, completed, isLocked, onSelect }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const activeGroup = groups.find(g => g.lessons.some(l => l.id === activeId))?.id;
  const [open, setOpen] = useState(() => new Set([activeGroup || groups[0]?.id]));
  useEffect(() => { if (activeGroup) setOpen(s => new Set(s).add(activeGroup)); }, [activeGroup]);
  const toggle = (id) => setOpen(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const doneCount = flat.filter(l => completed.has(l.id)).length;
  const pct = flat.length ? Math.round((doneCount / flat.length) * 100) : 0;
  const brand = pickLocalized(course.brand, lang);
  const sub = stripHtml(pickLocalized(course.subtitle, lang) || pickLocalized(course.description, lang) || '');
  return (
    <aside data-testid="learn-sidebar" className="space-y-4">
      <div className="rounded-2xl bg-white border border-slate-200/80 shadow-[0_4px_24px_-12px_rgba(15,23,42,0.12)] p-5 flex gap-5">
        <div className="flex-1 min-w-0">
          <Link to={`/courses/${course.id}`} data-testid="learn-sidebar-course-title" className="block hover:text-blue-600 transition-colors">
            {brand && <div className="text-base font-extrabold text-slate-900 truncate" dir="auto">{brand}</div>}
            <div className="text-base font-medium text-slate-600 truncate mt-1" dir="auto">{pickLocalized(course.title, lang)}</div>
          </Link>
          {sub && <p className="text-xs text-slate-500 leading-relaxed mt-2.5 line-clamp-2">{sub}</p>}
          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-3"><div className="h-full rounded-full bg-blue-600 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
          <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
            <span data-testid="learn-progress-text">{L(`${doneCount} من ${flat.length} دروس مكتملة`, `${doneCount} of ${flat.length} lessons completed`)}</span>
            <span data-testid="learn-progress-pct" className="text-slate-500" dir="ltr">{pct}%</span>
          </div>
        </div>
        <img src={course.image} alt="" className="w-[120px] h-[124px] xl:w-[140px] xl:h-[146px] rounded-xl object-cover shrink-0 bg-slate-100 hidden sm:block" />
      </div>
      <div className="rounded-2xl bg-white border border-slate-200/80 shadow-[0_4px_24px_-12px_rgba(15,23,42,0.12)] overflow-hidden divide-y divide-slate-100">
        {groups.map(g => {
          const isOpen = open.has(g.id);
          const done = g.lessons.filter(l => completed.has(l.id)).length;
          return (
            <div key={g.id}>
              <button type="button" data-testid={`curriculum-section-${g.id}`} onClick={() => toggle(g.id)} aria-expanded={isOpen} className="w-full flex items-center gap-3 px-4 py-3.5 text-start hover:bg-slate-50/70 transition-colors">
                <ChevronDown className={`w-4 h-4 shrink-0 text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                <span className={`flex-1 min-w-0 text-sm font-extrabold truncate ${isOpen ? 'text-blue-700' : 'text-slate-900'}`}>{pickLocalized(g.title, lang)}</span>
                {!isOpen && <span className="text-xs text-slate-400" dir="ltr">{done} / {g.lessons.length}</span>}
                {!isOpen && <FolderOpen className="w-4 h-4 text-slate-400" />}
              </button>
              {isOpen && (
                <div className="px-2 pb-2 space-y-0.5">
                  {g.lessons.map((l, i) => <Row key={l.id} lesson={l} idx={i} active={l.id === activeId} done={completed.has(l.id)} locked={isLocked(l)} onClick={() => onSelect(l)} />)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
};
